<template lang="pug">
.w-text( :class=`
  [
    {
      focus: isFocused,
      filled: filled || toolbar,
      required,
    },
    ...inputFeedback.classes
  ]` )
  .input-wrapper
    label
      .label {{ label }}
      editor-content.textarea( :editor='editor' )
    .input-actions
      transition-group( name='fade' mode='out-in' )
        button.input-action( v-for='action in actions' )
  .bar( :class='barClass' )
  transition( name='fade' mode='out-in' appear )
    .feedback(
      v-if='feedback || isValid === false'
      :key='inputFeedback.type'
      :class='inputFeedback.classes' )
      | {{ inputFeedback.message }}

</template>

<script lang="js">
import isArray from 'lodash-es/isArray'
import isFunction from 'lodash-es/isFunction'
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'

export const mixin = {
  props: {
    value: String,
    required: Boolean,
  },
  methods: {
    input( value ) {
      this.$emit( 'input', value )
    },
    change( value ) {
      this.$emit( 'change', value )
    },
    focus() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).focus()
      }
    },
    blur() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).blur()
      }
    },
    select() {
      const input = this.$refs.input
      if ( input ) {
        (isArray( input ) ? input[0] : input).select()
      }
    },
  }
}

export default {
  name: 'TextWidget',
  mixins: [],
  components: { EditorContent },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'input'
    },
    required: Boolean,
    feedback: Object,
    actions: {
      type: Array,
      default: () => []
    },
    validate: [ Object, Array, Function ]
  },
  data() {
    return {
      editor: null,
      isFocused: false,
      validating: false,
      toolbar: {},
      errors: null,
    }
  },
  created() {},
  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
      ],
      onUpdate: () => {
        // HTML
        this.$emit( 'change', this.editor.getHTML() )
        
        // JSON
        // this.$emit( 'change', this.editor.getJSON() )
      },
      onFocus: ({ editor,  event }) => {
        this.isFocused = true
        this.$emit( 'focus', event )
      },
      onBlur: ({ editor,  event }) => {
        this.isFocused = false
        this.$emit( 'focus', event )
      }
    })
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  methods: {
    async doValidate( value ) {
      if ( !this.validate && !this.required ) return
      this.validating = true
      this.errors = null
      const validation = await Promise.all(
        this.validations.map( async validator => {
          const res = await validator.validate( value )
          if ( res ) {
            return {
              type: validator.name,
              error: res
            }
          }
        }))
      this.validating = false
      this.errors = validation.filter( x => x )
    },
    focus() {
      const input = this.editor
      if ( input ) {
        input.view.dom.focus()
      }
    },
    blur() {
      const input = this.editor
      if ( input ) {
        input.view.dom.blur()
      }
    },
    select() {
      const input = this.editor
      if ( input ) {
        input.view.dom.select()
      }
    },
  },
  computed: {
    inputFeedback() {
      if ( this.feedback ) {
        const feedback = Object.assign({
          type: '',
          classes: this.feedback && this.feedback.type,
          message: ''
        }, this.feedback )
        return feedback
      }
      if ( this.isValid === false ) {
        return {
          type: 'error',
          classes: [ 'error', 'invalid' ],
          message: this.errors[0].error
        }
      }
      return {
        type: null,
        message: null
      }
    },
    barClass() {
      return ''
    },
    filled() {
      return !!this.value
    },
    validations() {
      let valid = []
      if ( isArray( this.validate )) {
        valid = this.validate
      } else if ( isFunction( this.validate )) {
        valid = [{
          name: 'function',
          validate: this.validate
        }]
      } else if ( this.validate ) {
        valid = [ this.validate ]
        // TODO: if Object.keys( this.validate ).length === 1 &&
        // Objec.values( this.validate )[0].name
      }
      const validations = this.required
            ? [{
              name: 'required',
              validate: function ( value ) {
                if ( !value ) {
                  return 'this field is required'
                }
              }
            }, ...valid ]
            : valid.length ? valid : null
      return validations
    },
    isValid () {
      if ( !this.errors ) return null
      return !this.errors.length
    }
  },
  watch: {
    value( input ) {
      // HTML
      const isSame = this.editor.getHTML() === input

      // JSON
      // const isSame = this.editor.getJSON().toString() === value.toString()

      if ( isSame ) {
        return
      }

      this.editor.commands.setContent( input, false )
    },
  }
}
</script>

<style lang="scss">
.w-text {
  position: relative;
  width: var(--w-input, 100%);
  margin-bottom: var(--m-input, 1em);
  padding-bottom: var(--p-input, 1em);
  font-size: var(--fs-input, var(--s-lg));
  opacity: 0.8;
  transition: width,height,opacity 250ms;
  .input-wrapper {
    display: flex;
    flex-wrap: nowrap;
    border-bottom: 2px solid var(--c-input-border, rgba(255,255,255,0.25));
    transition: border-bottom-color 250ms;
    > label {
      flex: 1 1 100%;
      display: block;
      position: relative;
      width: 100%;
      padding-top: 0.75em;
      margin: 0;
      .label {
        padding: 0;
        transition: 150ms all;
        font-size: 1em;
        position: absolute;
        top: 0.75em;
        left: 0;
        line-height: inherit;
        color: var(--c-text, black);
      }
    }
    .textarea {
      background: transparent;
      color: var(--c-text-input, var(--c-text, white));
      border: var(--b-input, 0 none);
      border-radius: var(--br-input, 0);
      width: 100%;
      border: 0 none !important;
      outline: 0 none !important;
      &:focus {
        border: 0 none !important;
        outline: 0 none !important;
      }
      > .ProseMirror {
        min-height: var(--h-textarea, 8rem);
        text-align: left;
        :first-child {
          margin-top: 0;
        }
        :last-child {
          margin-bottom: 0;
        }
      }
    }
  }
  .bar {
    transition: 250ms all;
    height: 2px;
    width: 0%;
    /* background: rgba(255,255,255,0.25); */
    background: rgba(0,120,255, 1);
    margin-top: -2px;
  }
  &:hover, &:focus, &.focus {
    opacity: 1;
    .input-wrapper {
    border-bottom-color: var(--c-input-border-active, rgba(255,255,255,0.4));
    }
  }
  &.focus {
    .input-wrapper {
      > label {
        .label {
          font-size: 0.65em;
          top: 0;
        }
      }
    }
    .bar {
      background: rgba(0,120,255, 1);
      width: 100%;
    }
  }
  &.filled {
    .input-wrapper {
      > label {
        .label {
          font-size: 0.65em;
          top: 0;
        }
      }
    }
  }
  &.required {
    .input-wrapper {
      > label {
        .label {
          &::after {
            content: " *"
          }
        }
      }
    }
  }
  .feedback {
    text-align: left;
    font-size: var(--s-3q);
    position: absolute;
    left: var(--pos-feedback-left, 0);
    right: var(--pos-feedback-right, unset);
    bottom: var(--pos-feedback-bottom, 0);
    &.warning {
      color: yellow
    }
    &.error {
      color: red
    }
    &.success {
      color: lime
    }
  }
  &.warning, &.error {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: yellow;
          }
        }
      }
    }
    .bar {
      width: 100%;
    }
  }
  &.warning {
    .bar {
      background: yellow
    }
  }
  &.error {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: red;
          }
        }
      }
    }
    .bar {
      background: red
    }
  }
  &.success {
    .input-wrapper {
      > label {
        .label {
          &::after {
            color: lime;
          }
        }
      }
      border-color: green;
    }
    .bar {
      background: lime;
    }
  }
}
</style>
